import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Download, Smartphone, Monitor, Apple, Chrome, Share, MoreVertical, Plus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLocale } from "@/contexts/LocaleContext";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const Install = () => {
  const { t } = useLocale();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
    }

    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(userAgent));
    setIsAndroid(/android/.test(userAgent));

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Listen for successful install
    window.addEventListener("appinstalled", () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setIsInstalled(true);
    }
    setDeferredPrompt(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/10 text-primary mb-6">
              <Download className="w-4 h-4" />
              <span className="text-sm font-medium">Install Callyout</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Get the <span className="text-primary">Callyout</span> App
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Install Callyout on your device for the best experience. Quick access, offline support, and instant predictions.
            </p>
          </motion.div>

          {/* Install Status */}
          {isInstalled && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-8 p-6 rounded-2xl bg-primary/10 border border-primary/30 text-center"
            >
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                <Check className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">App Already Installed!</h3>
              <p className="text-muted-foreground">
                You already have Callyout installed. Open it from your home screen or app drawer.
              </p>
            </motion.div>
          )}

          {/* Quick Install Button */}
          {deferredPrompt && !isInstalled && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-12 text-center"
            >
              <Button
                onClick={handleInstallClick}
                size="lg"
                className="gap-2 text-lg px-8 py-6"
              >
                <Download className="w-5 h-5" />
                Install Now
              </Button>
            </motion.div>
          )}

          {/* Platform-specific Instructions */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* iOS Instructions */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className={`p-6 rounded-2xl border ${isIOS ? "border-primary bg-primary/5" : "border-border bg-card"}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center">
                  <Apple className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">iPhone & iPad</h3>
                  <p className="text-sm text-muted-foreground">Safari Browser</p>
                </div>
                {isIOS && (
                  <span className="ml-auto text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                    Your Device
                  </span>
                )}
              </div>
              
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">1</span>
                  <span>Open this page in <strong>Safari</strong> browser</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">2</span>
                  <div className="flex items-center gap-2">
                    <span>Tap the</span>
                    <Share className="w-4 h-4" />
                    <span><strong>Share</strong> button</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">3</span>
                  <div className="flex items-center gap-2">
                    <span>Scroll down and tap</span>
                    <Plus className="w-4 h-4" />
                    <span><strong>Add to Home Screen</strong></span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">4</span>
                  <span>Tap <strong>Add</strong> to confirm</span>
                </li>
              </ol>
            </motion.div>

            {/* Android Instructions */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className={`p-6 rounded-2xl border ${isAndroid ? "border-primary bg-primary/5" : "border-border bg-card"}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">Android</h3>
                  <p className="text-sm text-muted-foreground">Chrome Browser</p>
                </div>
                {isAndroid && (
                  <span className="ml-auto text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                    Your Device
                  </span>
                )}
              </div>
              
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">1</span>
                  <span>Open this page in <strong>Chrome</strong> browser</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">2</span>
                  <div className="flex items-center gap-2">
                    <span>Tap the menu</span>
                    <MoreVertical className="w-4 h-4" />
                    <span>button (⋮)</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">3</span>
                  <span>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">4</span>
                  <span>Tap <strong>Install</strong> to confirm</span>
                </li>
              </ol>
            </motion.div>

            {/* Desktop Instructions */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className={`p-6 rounded-2xl border ${!isIOS && !isAndroid ? "border-primary bg-primary/5" : "border-border bg-card"}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                  <Monitor className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">Desktop</h3>
                  <p className="text-sm text-muted-foreground">Chrome, Edge, or Brave</p>
                </div>
                {!isIOS && !isAndroid && (
                  <span className="ml-auto text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                    Your Device
                  </span>
                )}
              </div>
              
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">1</span>
                  <span>Look for the <strong>install icon</strong> in the address bar</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">2</span>
                  <span>Click the icon and select <strong>Install</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">3</span>
                  <span>The app will open in its own window</span>
                </li>
              </ol>
            </motion.div>

            {/* Chrome Instructions */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="p-6 rounded-2xl border border-border bg-card"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500 via-red-500 to-green-500 flex items-center justify-center">
                  <Chrome className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">Any Chrome Browser</h3>
                  <p className="text-sm text-muted-foreground">Alternative Method</p>
                </div>
              </div>
              
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">1</span>
                  <span>Type <code className="bg-muted px-1.5 py-0.5 rounded text-xs">chrome://apps</code> in address bar</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0 text-xs font-medium">2</span>
                  <span>Find Callyout and pin it to taskbar</span>
                </li>
              </ol>
            </motion.div>
          </div>

          {/* Features */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-center"
          >
            <h2 className="text-2xl font-bold mb-6">Why Install?</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-card border border-border">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <Smartphone className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold mb-1">Quick Access</h3>
                <p className="text-sm text-muted-foreground">Launch instantly from your home screen</p>
              </div>
              <div className="p-4 rounded-xl bg-card border border-border">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <Download className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold mb-1">Works Offline</h3>
                <p className="text-sm text-muted-foreground">Browse events even without internet</p>
              </div>
              <div className="p-4 rounded-xl bg-card border border-border">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <Monitor className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold mb-1">Full Screen</h3>
                <p className="text-sm text-muted-foreground">No browser UI for immersive experience</p>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Install;
